import React, { useContext, useRef, useCallback } from "react";
import { GoogleMap, LoadScript, Marker, InfoWindow } from "@react-google-maps/api";
import classes from "./Map.module.scss";
import colourClasses from "../../colour.module.scss";
import GoalContext from "../../store/goal-context";
import mapStyles from "./map-styles";
import MapLocationInfo from "../MapLocationInfo/MapLocationInfo";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { Swiper, SwiperSlide } from 'swiper/react';

const containerStyle = {
  width: "100%",
  height: "100%",
};

const center = {
  lat: 56.4746004,
  lng: -2.9668333,
};

const mapOptions = {
  styles: mapStyles,
};

const Map = () => {
  const map = useRef();
  const goalContext = useContext(GoalContext);
  let locations = null;
  let addressLocations = [];

  if (goalContext.activeGoal !== null) {
    locations = goalContext.goals[goalContext.activeGoal - 1].locations;
    if(locations) {
      Object.keys(locations).map((index, locationVal) => {
        const addresses = locations[index].addresses;
        if(addresses) {
          Object.keys(addresses).map((key, value) => {
              const marker = locations[index].map_pin ? locations[index].map_pin : goalContext.goals[goalContext.activeGoal -1].map_pin;
              const markerObject = {
                  goalId: parseInt(goalContext.goals[goalContext.activeGoal - 1].number),
                  locationId: parseInt(index),
                  name: locations[index].name,
                  link: locations[index].link,
                  description: locations[index].description,
                  address: addresses[key].address.name,
                  lat: addresses[key].address.lat,
                  lng: addresses[key].address.lng,
                  primaryColour: goalContext.goals[goalContext.activeGoal -1].primary_colour ? `colour--${goalContext.goals[goalContext.activeGoal -1].primary_colour}` : 'colour--black',
                  secondaryColour: goalContext.goals[goalContext.activeGoal -1].secondary_colour ? `colour--${goalContext.goals[goalContext.activeGoal -1].secondary_colour}` : 'colour--black--accent',
                  pin: marker
              }
              addressLocations.push(markerObject);
          });
        }
      });
    }
  } else {
    const goals = goalContext.goals;
    for(let i=0; i < goals.length; i++) {
      locations = goalContext.goals[i].locations;
      if(locations) {
        Object.keys(locations).map((index, locationVal) => {
          const addresses = locations[index].addresses;
          if(addresses) {
            Object.keys(addresses).map((key, value) => {
                const marker = locations[index].map_pin ? locations[index].map_pin : goalContext.goals[i].map_pin;
                const markerObject = {
                    goalId: parseInt(goals[i].number),
                    locationId: parseInt(index),
                    name: locations[index].name,
                    link: locations[index].link,
                    description: locations[index].description,
                    address: addresses[key].address.name,
                    lat: addresses[key].address.lat,
                    lng: addresses[key].address.lng,
                    primaryColour: goalContext.goals[i].primary_colour ? `colour--${goalContext.goals[i].primary_colour}` : 'colour--black',
                    secondaryColour: goalContext.goals[i].secondary_colour ? `colour--${goalContext.goals[i].secondary_colour}` : 'colour--black--accent',
                    pin: marker
                }
                addressLocations.push(markerObject);
            });
          }
        })
      }
    }
  }

  const setOpacityHandler = (data) => {
    const goalIsActive = goalContext.activeGoal;
    const locationIsActive = goalContext.activeLocation;
    if(goalIsActive === null && locationIsActive === null) {
      return 1;
    } else if(locationIsActive !== null && data.id == locationIsActive) {
      return 1;
    } else if (goalIsActive !== null && locationIsActive === null) {
      return 1; 
    }else {
      return 0.2;
    }
  }

  const markerHandler = (marker) => {
    if(goalContext.activeGoal == null) {
      goalContext.setActiveGoal(marker.goal);
    }
    const goalAnchor = document.querySelector(`#goal-${marker.goal}`);
    goalAnchor.scrollIntoView({
        behavior: 'auto'
    });

    const addresses = goalContext.goals[marker.goal - 1].locations[marker.location].addresses;

    if(addresses.length > 1) {
      const bounds = new window.google.maps.LatLngBounds();
      for(let i=0; i < addresses.length; i++) {
        let point = new window.google.maps.LatLng(
          parseFloat(addresses[i].address.lat),
          parseFloat(addresses[i].address.lng)
        );
        bounds.extend(point);
      }
      goalContext.activeMapInstance.instance.fitBounds(bounds);
    } else{
      goalContext.activeMapInstance.instance.zoom = 16;
      goalContext.activeMapInstance.instance.panTo({
        lat: marker.lat,
        lng: marker.lng
      });
    }

    goalContext.setActiveLocation(marker.location);
    if (marker.location === goalContext.activeMarker) {
      return;
    }

    goalContext.setActiveMarker(marker.location);


    setTimeout(() => {
      goalContext.activeSwiper.instance.slideTo(marker.location); 
    }, 500)

    setTimeout(() => {
      const locationAnchor = document.querySelector(`#location-${marker.location}`);
      locationAnchor.scrollIntoView({
        behavior: 'smooth'
      });
    }, 500)
  };

  const onLoad = useCallback(
    function onLoad (mapInstance) {
      goalContext.setMapInstance({instance: mapInstance});
    }
  )

  return (
    <div id="map">
      <LoadScript googleMapsApiKey="AIzaSyA2ItoGn5SIBJI7sX8BIMK78Et26Gjt_Ao">
        <div
          className={`${classes["google-map-holder"]} ${
            goalContext.activeGoal !== null ? classes.active : ""
          }`}
        >
          <GoogleMap
            onLoad={onLoad}
            ref={map}
            options={mapOptions}
            mapContainerStyle={containerStyle}
            center={center}
            zoom={13}
          >
            {locations &&
              Object.keys(addressLocations).map((key, value) => (
                <Marker
                  opacity={setOpacityHandler({id: addressLocations[key].locationId})}
                  icon={addressLocations[key].pin}
                  title={addressLocations[key].name}
                  animation={2}
                  key={value}
                  onClick={markerHandler.bind("SET", {
                    location: addressLocations[key].locationId,
                    goal: addressLocations[key].goalId,
                    lat: addressLocations[key].lat,
                    lng: addressLocations[key].lng,
                  })}
                  position={{
                    lat: parseFloat(addressLocations[key].lat),
                    lng: parseFloat(addressLocations[key].lng),
                  }}
                >

                {goalContext.activeMarker === addressLocations[key].locationId ? (
                  <InfoWindow onCloseClick={() => goalContext.setActiveMarker(null)}>
                    <div className={classes.infoBox}>
                      <h3 className={colourClasses[addressLocations[key].primaryColour]}>{addressLocations[key].name}</h3>
                      <p>{addressLocations[key].description}</p>
                      <div className={`${classes.visitWebsiteInfoBox}`}><a href={addressLocations[key].link} target="_blank" rel="noopener noreferrer" className={`${colourClasses[addressLocations[key].secondaryColour]}`}>Visit Website <FontAwesomeIcon className={colourClasses[addressLocations[key].primaryColour]} icon={faArrowUpRightFromSquare} /></a></div>
                    </div>
                  </InfoWindow>
                ) : null}

                </Marker>
              ))}
          </GoogleMap>
        </div>
      </LoadScript>
      <MapLocationInfo />
    </div>
  );
};

export default Map;
